import {
  CheckIcon, ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon,
  PlusIcon, XMarkIcon,
} from '@heroicons/react/24/solid';
import { Radio } from 'flowbite-react';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback, useEffect, useMemo,
  useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { formatValue } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import {
  COPRO_TYPE_FILTER_COMPANY_COPRO, COPRO_TYPE_FILTER_SUBCONTRACTED_COPRO,
  COPRO_TYPE_FILTER_SUBCONTRACTOR_FOR_COPRO,
  coproTypes,
} from '../../../utils/copros';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import ParcelleSvg from '../../common/ParcelleSvg';
import Popup from '../../common/Popup';
import RedirectionMapButtons from '../../common/RedirectionMapButtons';
import { SetupPage, updateSetupState } from '../../common/SetupPage';
import Spinner from '../../common/Spinner';
import {
  calculateAdjustedEstimation,
  calculateTotalSurface,
  calculateValidatedBuildings,
  ROOF_TYPE_MAP,
  ROOF_TYPE_REVERSE_MAP,
} from './utils';

const headers = [
  { title: 'N° IMMAT' },
  { title: 'NOM' },
  { title: 'ADRESSE' },
  { title: 'NB LOTS HAB / LOCAL PRO' },
  { title: 'TYPE' },
  { title: 'NB BATIMENTS' },
  { title: 'Surface validée de votre copro (m²)' },
  { title: 'Actions' },
];

const sortableColumns = [
  'N° IMMAT', 'NOM', 'ADRESSE', 'NB LOTS HAB / LOCAL PRO', 'TYPE', 'NB BATIMENTS', 'Surface validée de votre copro (m²)',
];

const buildingHeaders = [
  { title: 'Batiment' },
  { title: 'Aperçu' },
  { title: 'CAPTURAMA sans comble (m²)' },
  { title: 'CAPTURAMA avec comble (m²)' },
  { title: 'CAPTURAMA étages' },
  { title: 'BATI étages' },
  { title: 'Validation' },
  { title: 'Type de toiture (utilisé dans l\'estimation ajustée)' },
  { title: 'Nombre d\'étage réel (utilisé dans l\'estimation ajustée)' },
  { title: 'Estimation ajustée (m²)' },
  { title: 'Surface réelle (m²)' },
];

function CoproArea() {
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const dispatch = useAppDispatch();

  const [copros, setCopros] = useState(null);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRoofType, setPendingRoofType] = useState(null);
  const [pendingBatimentIdx, setPendingBatimentIdx] = useState(null);
  const [selectedCopro, setSelectedCopro] = useState(null);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [isTop, setIsTop] = useState(true);
  const [isBottom, setIsBottom] = useState(false);
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  async function fetchDatas() {
    setStatus('PENDING');
    try {
      const [buildingsResponse, surfaceResponse] = await Promise.all([
        fetch(`/api/companies/${company.id}/copros/buildings`, JWTHeader(jwt)),
        fetch(`/api/companies/${company.id}/copros/bati`, JWTHeader(jwt)),
      ]);

      const surfaceResult = await surfaceResponse.json();
      const buildingsResult = await buildingsResponse.json();

      // Create a map of surface data keyed by copro id
      const registeredCopro = surfaceResult.map((copro) => {
        const buildingsData = buildingsResult.find((coproBuildings) => copro.id === coproBuildings.id)?.buildings || [];

        // Map buildings data to surface data
        const mappedBatiments = buildingsData.map((building) => {
          const surfaceBatiment = copro.batiments.find((batiment) => batiment.batimentId === building.remoteId) || {};
          return {
            ...surfaceBatiment,
            id: building.id || null,
            batimentId: building.remoteId,
            realFloorCount: building.floors || surfaceBatiment.nbNiveaux || 0,
            realSurface: building.area || '',
            realRoofType: building.roofType && ROOF_TYPE_REVERSE_MAP[building.roofType] ? ROOF_TYPE_REVERSE_MAP[building.roofType] : 'ne_pas_utiliser',
            isValidatedByUser: building.validatedByUser ?? null,
          };
        });

        // Add any remaining surface data batiments that weren't in buildings data
        const remainingSurfaceBatiments = copro.batiments.filter(
          (batiment) => !mappedBatiments.some((b) => b.batimentId === batiment.batimentId),
        ).map((batiment) => ({
          ...batiment,
          id: null,
          batimentId: batiment.batimentId,
          realRoofType: 'ne_pas_utiliser',
          realFloorCount: batiment.nbNiveaux || 0,
          realSurface: '',
          isValidatedByUser: null,
          adjustedEstimation: 0,
        }));

        return {
          id: copro.id,
          'N° IMMAT': copro.remoteId,
          NOM: copro.name,
          ADRESSE: copro.address !== null ? (
            <div className="flex items-center">
              <div>
                <RedirectionMapButtons address={copro.address} />
              </div>
              <span>{copro.address}</span>
            </div>
          ) : '',
          'NB LOTS HAB / LOCAL PRO': copro.batchSize,
          TYPE: coproTypes[copro.type],
          batiments: [...mappedBatiments, ...remainingSurfaceBatiments],
          company: { id: copro.companyId },
          'Type de copropriété': (() => {
            if (copro.companyId === company.id) {
              return COPRO_TYPE_FILTER_COMPANY_COPRO;
            } if (company.subcontractors.some((subcontractor) => subcontractor.id === copro.companyId)) {
              return COPRO_TYPE_FILTER_SUBCONTRACTED_COPRO;
            } if (company.subcontractorFor.some((subcontractorForData) => subcontractorForData.id === copro.companyId)) {
              return COPRO_TYPE_FILTER_SUBCONTRACTOR_FOR_COPRO;
            }
            return null; // or a default value if none of the conditions are met
          })(),
        };
      });

      setCopros(registeredCopro);
      setStatus('SUCCESS');
    } catch (error) {
      console.error('Error fetching copros :', error);
      setStatus('ERROR');
      setErrorMessage('Erreur lors du chargement des copropriétés');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  }

  const updateBuilding = async(building) => {
    try {
      const response = await fetch(`/api/buildings/${building.id}`, {
        method: 'PUT',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          remoteId: building.batimentId,
          floors: parseInt(building.realFloorCount, 10),
          roofType: ROOF_TYPE_MAP[building.realRoofType],
          area: parseFloat(building.realSurface),
          validatedByUser: building.isValidatedByUser,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update building');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating building :', error);
      showToast('error', 'Une erreur est survenue lors de la mise à jour du batiment.');
    }
  };

  const createBuildingsBatch = async(buildings) => {
    try {
      const formattedBuildings = buildings.map((building) => ({
        remoteId: building.batimentId,
        floors: parseInt(building.realFloorCount, 10),
        roofType: ROOF_TYPE_MAP[building.realRoofType],
        area: parseFloat(building.realSurface),
        validatedByUser: building.isValidatedByUser,
      }));

      const response = await fetch(`/api/copros/${selectedCopro.id}/buildings/batch`, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedBuildings),
      });

      if (!response.ok) {
        throw new Error('Failed to create buildings batch');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating buildings batch :', error);
      showToast('error', 'Une erreur est survenue lors de la création des batiments.');
      throw error;
    }
  };

  const handleSaveBuildings = async(buildings) => {
    setIsSaving(true);
    try {
      const buildingsToUpdate = buildings.filter((building) => building.id);
      const buildingsToCreate = buildings.filter((building) => !building.id);

      const updatePromises = buildingsToUpdate.map(updateBuilding);
      const createPromise = buildingsToCreate.length > 0 ? createBuildingsBatch(buildingsToCreate) : Promise.resolve([]);

      const [updatedBuildings, createdBuildings] = await Promise.all([
        Promise.all(updatePromises),
        createPromise,
      ]);

      const allUpdatedBuildings = [...updatedBuildings, ...createdBuildings];

      // Check if all promises resolved successfully
      if (allUpdatedBuildings.every((building) => building)) {
        setCopros((prevCopros) => prevCopros.map((copro) => {
          if (copro.id === selectedCopro.id) {
            return {
              ...copro,
              batiments: selectedCopro.batiments.map((existingBatiment) => {
                const updatedBatiment = allUpdatedBuildings.find((b) => b.id === existingBatiment.id || b.remoteId === existingBatiment.batimentId);
                if (updatedBatiment) {
                  return {
                    ...existingBatiment,
                    ...updatedBatiment,
                    id: updatedBatiment.id || existingBatiment.id,
                    batimentId: updatedBatiment.remoteId || existingBatiment.batimentId,
                    realFloorCount: updatedBatiment.floors,
                    realSurface: updatedBatiment.area,
                    realRoofType: ROOF_TYPE_REVERSE_MAP[updatedBatiment.roofType] || 'ne_pas_utiliser',
                    isValidatedByUser: updatedBatiment.validatedByUser,
                  };
                }
                return existingBatiment;
              }),
            };
          }
          return copro;
        }));

        showToast('success', 'Les batiments ont bien été sauvegardés.');
      } else {
        throw new Error('Some buildings failed to update or create');
      }
    } catch (error) {
      console.error('Error saving buildings :', error);
      showToast('error', 'Une erreur est survenue lors de la sauvegarde des batiments.');
      fetchDatas(); // Refetch data in case of error
    } finally {
      setIsSaving(false);
      setSelectedCopro(null);
    }
  };

  const handleInputChange = (batimentIndex, field, value) => {
    setSelectedCopro((prevSelectedCopro) => {
      if (prevSelectedCopro) {
        const updatedBatiments = prevSelectedCopro.batiments.map((batiment, index) => {
          if (index === batimentIndex) {
            const updatedBatiment = { ...batiment, [field]: value.replace(',', '.') };
            const calcResult = calculateAdjustedEstimation(updatedBatiment, updatedBatiment.realFloorCount, updatedBatiment.realRoofType);
            return {
              ...updatedBatiment,
              adjustedEstimation: calcResult.totalHabitableAvg ? Math.round(calcResult.totalHabitableAvg * 100) / 100 : 0,
            };
          }
          return batiment;
        });
        return { ...prevSelectedCopro, batiments: updatedBatiments };
      }
      return prevSelectedCopro;
    });
  };

  const updateRoofType = (batimentIndex, value) => {
    setSelectedCopro((prevSelectedCopro) => {
      if (prevSelectedCopro) {
        const updatedBatiments = prevSelectedCopro.batiments.map((batiment, index) => {
          if (index === batimentIndex) {
            const updatedBatiment = { ...batiment, realRoofType: value };
            const calcResult = calculateAdjustedEstimation(updatedBatiment, updatedBatiment.realFloorCount, value);
            return {
              ...updatedBatiment,
              adjustedEstimation: calcResult.totalHabitableAvg ? Math.round(calcResult.totalHabitableAvg * 100) / 100 : 0,
            };
          }
          return batiment;
        });
        return { ...prevSelectedCopro, batiments: updatedBatiments };
      }
      return prevSelectedCopro;
    });
  };

  const handleRoofTypeChange = (batimentIndex, value) => {
    if (value !== 'ne_pas_utiliser' && selectedCopro.batiments[batimentIndex].realRoofType == 'ne_pas_utiliser') {
      setPendingRoofType(value);
      setPendingBatimentIdx(batimentIndex);
      setShowConfirmation(true);
    } else {
      updateRoofType(batimentIndex, value);
    }
  };

  useEffect(() => {
    if (company && company.id) {
      fetchDatas();
    }
  }, [company]);

  // Function to sort copros with unvalidated ones at the top
  const sortCoprosByValidation = (coproList) => coproList.sort((a, b) => {
    const aValidatedSurface = calculateTotalSurface(a, true);
    const bValidatedSurface = calculateTotalSurface(b, true);
    return Number(aValidatedSurface) === 0 ? -1 : Number(bValidatedSurface) === 0 ? 1 : 0;
  });

  const sortedCopros = useMemo(() => {
    if (!copros) {return null;}
    return sortCoprosByValidation(copros);
  }, [copros]);

  const handleConfirmation = (confirmed) => {
    setShowConfirmation(false);
    if (confirmed) {
      updateRoofType(pendingBatimentIdx, pendingRoofType);
    }
    setPendingRoofType(null);
    setPendingBatimentIdx(null);
  };

  const handleDetailsClick = (copro) => {
    // this implementation avoid precomputing on the frontend the adjusted estimation
    // for every copro and batiment , but only do it when the user select a copro
    // as it could impact the performances

    setSelectedCopro(selectedCopro?.id === copro.id ? null : {
      ...copro,
      batiments: copro.batiments.map((batiment) => ({
        ...batiment,
        adjustedEstimation: calculateAdjustedEstimation(batiment, batiment.nbNiveaux, 'ne_pas_utiliser').totalHabitableAvg,
      })),
    });
  };

  const handleValidationChange = (batimentIndex, isValid) => {
    setSelectedCopro((prevSelectedCopro) => {
      if (prevSelectedCopro) {
        const updatedBatiments = prevSelectedCopro.batiments.map((batiment, index) => {
          if (index === batimentIndex) {
            return { ...batiment, isValidatedByUser: isValid };
          }
          return batiment;
        });
        return { ...prevSelectedCopro, batiments: updatedBatiments };
      }
      return prevSelectedCopro;
    });
  };

  const addManualBuilding = () => {
    setSelectedCopro((prevSelectedCopro) => {
      if (prevSelectedCopro) {
        const newBuilding = {
          batimentId: null,
          geometry: null,
          habitableHorsCombles: null,
          habitableTotalMoy: null,
          nbNiveaux: null,
          nombreDEtages: null,
          realFloorCount: 0,
          realSurface: 0,
          realRoofType: 'ne_pas_utiliser',
          isValidatedByUser: true,
          adjustedEstimation: 0,
        };
        return {
          ...prevSelectedCopro,
          batiments: [...prevSelectedCopro.batiments, newBuilding],
        };
      }
      return prevSelectedCopro;
    });
  };

  const isAllBuildingsValidated = useCallback(() => selectedCopro?.batiments.every((building) => building.isValidatedByUser !== null), [selectedCopro]);

  const handleSaveClick = () => {
    if (isAllBuildingsValidated()) {
      handleSaveBuildings(selectedCopro.batiments);
    } else {
      setShowValidationWarning(true);
    }
  };

  const renderBuildingTable = useCallback((buildings) => {
    const totalSurface = calculateTotalSurface(selectedCopro);
    const validatedBuildings = calculateValidatedBuildings(selectedCopro);

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 1, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden rounded-b-[20px] border-x border-b px-4"
        >
          <h2 className="mt-4 mb-4 text-xl font-bold">
            Détails des batiments pour{' '}
            {selectedCopro.NOM}
          </h2>

          <p>
            {buildings.length === 1 ? 'Pour le batiment' : 'Pour chacun des batiments :'}
          </p>
          <ul className="mb-4 list-inside list-disc">
            <li>Indiquez si le batiment fait partie de la copropriété.</li>
            <li>Indiquez le nombre d'étages réel du batiment.</li>
            <li>Indiquez la surface réelle du batiment.</li>
          </ul>
          <p className="mb-4">
            <b>Attention :</b>
            {' '}
            veillez à compléter les informations le plus justement possible car celles-ci sont utilisées dans le calcul des tentièmes de copropriété.
          </p>

          {buildings.length === 0 ? (
            <Badge
              mode="error"
              text="Aucun batiment détecté automatiquement, veuillez les ajouter manuellement"
              className="mb-4"
            />
          ) : (
            <MUITable
              data={buildings.map((building, index) => {
                const isNotPartOfCopro = building.isValidatedByUser === false;
                return {
                  Batiment: `Batiment ${index + 1}`,
                  Aperçu: <div className="min-w-52"><ParcelleSvg geometry={building.geometry} maxHeight={250} showSatellite /></div>,
                  'CAPTURAMA sans comble (m²)': formatValue(building.habitableHorsCombles, 1 || 0),
                  'CAPTURAMA avec comble (m²)': formatValue(building.habitableTotalMoy, 1 || 0),
                  'CAPTURAMA étages': building.nbNiveaux,
                  'BATI étages': building.nombreDEtages,
                  Validation: (
                    <div className="flex min-w-32 flex-col space-y-2">
                      <button
                        className={`rounded px-2 py-1 font-semibold ${building.isValidatedByUser === true ? 'bg-rgpd-green text-white hover:bg-rgpd-green-dark' : 'bg-gray-200 hover:bg-gray-300'}`}
                        onClick={() => handleValidationChange(index, true)}
                      >
                        Fait partie de la copro
                      </button>
                      <button
                        className={`rounded px-2 py-1 font-semibold ${building.isValidatedByUser === false ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                        onClick={() => handleValidationChange(index, false)}
                      >
                        Ne fait pas partie de la copro
                      </button>
                    </div>
                  ),
                  'Type de toiture (utilisé dans l\'estimation ajustée)': (
                    <div className={`flex items-center justify-center ${isNotPartOfCopro ? 'pointer-events-none opacity-50' : ''}`}>
                      <div className="flex space-x-4">
                        {['ne_pas_utiliser', 'ardoise', 'tuile', 'autre'].map((type) => (
                          <label key={type} className="flex items-center space-x-2">
                            <Radio
                              name={`roofType-${index}`}
                              value={type}
                              checked={building.realRoofType === type}
                              onChange={() => handleRoofTypeChange(index, type)}
                              disabled={isNotPartOfCopro}
                            />
                            <span className="text-sm font-medium text-gray-900">
                              {type === 'ne_pas_utiliser' ? 'Ne pas utiliser' : type.charAt(0).toUpperCase() + type.slice(1)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ),
                  'Nombre d\'étage réel (utilisé dans l\'estimation ajustée)': (
                    <input
                      type="number"
                      value={building.realFloorCount || ''}
                      onChange={(e) => handleInputChange(index, 'realFloorCount', e.target.value)}
                      className={`w-20 rounded border p-1 ${isNotPartOfCopro ? 'opacity-50' : ''}`}
                      disabled={isNotPartOfCopro}
                    />
                  ),
                  'Estimation ajustée (m²)': (
                    <span className={isNotPartOfCopro ? 'opacity-50' : ''}>
                      {building.adjustedEstimation ? formatValue(building.adjustedEstimation, 1) : 0}
                    </span>
                  ),
                  'Surface réelle (m²)': (
                    <div className="flex flex-col gap-y-2">
                      <button
                        className={`inline rounded px-2 py-1 text-xs text-white ${
                          isNotPartOfCopro
                            ? 'cursor-not-allowed bg-gray-400 opacity-50'
                            : 'bg-rgpd-blue-dark'
                        }`}
                        onClick={() => {
                          if (!isNotPartOfCopro) {
                            handleInputChange(index, 'realSurface', formatValue(building.adjustedEstimation, 1));
                          }
                        }}
                        disabled={isNotPartOfCopro}
                      >
                        Utiliser l'estimation
                      </button>
                      <input
                        type="number"
                        value={building.realSurface}
                        onChange={(e) => handleInputChange(index, 'realSurface', e.target.value)}
                        className={`mb-12 w-20 rounded border p-1 ${isNotPartOfCopro ? 'opacity-50' : ''}`}
                        disabled={isNotPartOfCopro}
                      />
                    </div>
                  ),
                };
              })}
              headers={buildingHeaders}
              tableClass="min-w-full bg-white border text-sm"
              isSearchable={false}
              disablePagination
            />
          )}
          <div className="justify-between pb-4 md:flex">
            <button
              className="align-center mt-4 flex h-min w-full items-center justify-center self-end rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto"
              onClick={addManualBuilding}
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              {' '}
              Ajouter un batiment manuellement
            </button>
            {buildings.length > 0 && (
              <div className="mt-4 flex-col items-end md:flex">
                <p className="mb-4 text-lg font-semibold">
                  Surface totale de votre copropriété :
                  {' '}
                  {totalSurface}
                </p>
                <p className="mb-4 text-lg font-semibold">
                  Batiments validés :
                  {' '}
                  {validatedBuildings}
                </p>
                <button
                  className={`align-center flex w-full items-center justify-center rounded px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto ${
                    isAllBuildingsValidated() && !isSaving
                      ? 'bg-rgpd-green hover:bg-rgpd-green-dark'
                      : 'cursor-not-allowed bg-rgpd-green/80'
                  }`}
                  onClick={handleSaveClick}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Spinner size="sm" className="mr-2" />
                  ) : (
                    <CheckIcon className="mr-2 h-5 w-5" />
                  )}
                  {isSaving ? 'Sauvegarde en cours...' : 'Sauvegarder'}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }, [handleValidationChange, handleRoofTypeChange, handleInputChange, handleSaveBuildings, addManualBuilding, isAllBuildingsValidated, isSaving]);

  useEffect(() => {
    const topObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsTop(true);
        } else if (!entry.isIntersecting) {
          setIsTop(false);
        }
      },
      { threshold: 1 },
    );

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsBottom(true);
        } else if (!entry.isIntersecting) {
          setIsBottom(false);
        }
      },
      { threshold: 0 },
    );

    if (topRef.current) {
      topObserver.observe(topRef.current);
    }

    if (bottomRef.current) {
      bottomObserver.observe(bottomRef.current);
    }

    return () => {
      if (topRef.current) {
        topObserver.unobserve(topRef.current);
      }
      if (bottomRef.current) {
        bottomObserver.unobserve(bottomRef.current);
      }
    };
  }, [topRef, bottomRef]);

  const handleNextStep = async() => {
    if (status === 'PENDING' || status === 'IDLE') {
      return;
    }
    await updateSetupState(company, jwt, 'surfaceValidation', dispatch);
    navigate(ROUTES.SETUP_ADD_COLLABORATORS);
  };

  const renderSaveButton = () => {
    if (selectedCopro) {
      return null;
    }

    return (
      <div
        className={classNames(
          'text-right w-full md:w-auto mt-10',
          !isTop && !isBottom && 'fixed z-[1000] right-0 bottom-0 md:right-5 md:bottom-5 bg-white rounded',
        )}
      >
        <button
          onClick={handleNextStep}
          className={classNames(
            'bg-rgpd-green text-white text-xl px-8 py-4 shadow-md align-center w-full md:w-auto rounded',
            (status === 'PENDING' || status === 'IDLE') && 'opacity-80 cursor-not-allowed',
          )}
        >
          Prochaine étape
        </button>
      </div>
    );
  };

  return (
    <SetupPage
      step={'surfaceValidation'}
      title="SURFACE DE VOS COPROPRIÉTÉS"
      children={(
        <>
          <div ref={topRef} style={{ height: '1px' }} />
          <div className="mb-12">
            <p>
              Cette page permet de compléter les informations de surface de vos copropriétés.
              <br />
              Ces informations sont nécessaires pour le bon calcul des tentièmes de copropriété afin de vous aider dans la qualification des DPEs.
              <br />
              <br />
              <b>Attention :</b>
              {' '}
              le nombre d'étage réel et la surface réelle seront utilisés dans le calcul des tentièmes de copropriété.
            </p>
          </div>
          {status === 'SUCCESS' && (
            <div className="my-10">
              <MUITable
                data={sortedCopros?.filter((copro) => (!selectedCopro || (selectedCopro && selectedCopro.id === copro.id))).map((copro) => {
                  const isExpanded = selectedCopro && selectedCopro.id === copro.id;
                  const validatedSurface = calculateTotalSurface(copro, true);

                  return {
                    ...copro,
                    'NB BATIMENTS': copro.batiments.length,
                    'Surface validée de votre copro (m²)': validatedSurface.toFixed(0),
                    Actions: (copro['Type de copropriété'] === COPRO_TYPE_FILTER_COMPANY_COPRO
                      ? (
                        <button
                          className={`w-full ${Number(validatedSurface) > 0 ? 'bg-rgpd-green hover:bg-rgpd-green-dark' : 'hover:bg-rgpd-blue-dark-dark bg-rgpd-blue-dark'} align-center mr-2 flex items-center justify-center rounded px-5 py-3 text-base whitespace-nowrap text-white shadow-md`}
                          onClick={() => handleDetailsClick(copro)}
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUpIcon className="mr-2 h-5 w-5" />
                              {' '}
                              Revenir à la liste
                            </>
                          ) : (
                            <>
                              <ChevronDownIcon className="mr-2 h-5 w-5" />
                              {Number(validatedSurface) > 0 ? 'Voir la surface' : 'Valider la surface'}
                            </>
                          )}
                        </button>
                      ) : 'Copropriété sous-traitée'
                    ),
                  };
                })}
                headers={headers}
                tableClass="min-w-full bg-white border text-sm"
                sortableColumns={sortableColumns}
                isSearchable={!selectedCopro}
                disablePagination
                filterColumnName={!selectedCopro && sortedCopros && new Set(sortedCopros.map((copro) => copro['Type de copropriété'])).size > 1 ? 'Type de copropriété' : null}
              />
              {selectedCopro && (
                renderBuildingTable(selectedCopro.batiments)
              )}
              {copros && copros.length === 0 && (
                <div className="mx-10 my-10 text-center">
                  <Badge mode="warning" text="Aucune copropriété enregistrée" />
                </div>
              )}
              {showConfirmation && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-600/50"
                >
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.9 }}
                    className="rounded-lg bg-white p-5 shadow-xl"
                  >
                    <p className="mb-4">Etes-vous sûr de vouloir utiliser cette méthode de calcul ?</p>
                    <div className="flex justify-end space-x-2">
                      <button
                        className="focus:shadow-outline mt-4 ml-1 flex w-full items-center justify-center rounded bg-red-600 px-4 py-2 font-bold text-white opacity-90 hover:bg-red-700 focus:outline-none md:w-auto"
                        onClick={() => handleConfirmation(false)}
                      >
                        <XMarkIcon className="mr-2 h-5 w-5" />
                        {' '}
                        Non
                      </button>
                      <button
                        className="focus:shadow-outline mt-4 ml-1 flex w-full items-center justify-center rounded bg-rgpd-green px-4 py-2 font-bold text-white opacity-90 hover:bg-rgpd-green-dark focus:outline-none md:w-auto"
                        onClick={() => handleConfirmation(true)}
                      >
                        <CheckIcon className="mr-2 h-5 w-5" />
                        {' '}
                        Oui
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
              <Popup
                title={(
                  <div className="mb-4 flex items-center">
                    <ExclamationTriangleIcon className="mr-2 h-6 w-6 text-yellow-500" />
                    <p className="text-lg font-semibold">Attention</p>
                  </div>
                )}
                message="Veuillez indiquer pour chaque batiment s'il fait partie de la copropriété avant de sauvegarder."
                onClose={() => setShowValidationWarning(false)}
                isOpen={showValidationWarning}
              />
            </div>
          )}
          {status === 'ERROR' && (
            <div className="mx-10 my-32 text-center">
              <Badge mode="error" text={errorMessage} />
            </div>
          )}
          {status === 'PENDING' && (
            <div className="mx-10 my-32 text-center">
              <Spinner aria-label="Center-aligned spinner" size="xl" />
            </div>
          )}
          <div ref={bottomRef} style={{ height: '1px' }} />
          {renderSaveButton()}
        </>
      )}
    />
  );
}

export default CoproArea;
